/**
 * Offline Storage Service
 * 
 * Manages localStorage persistence for offline-first capability:
 * - Route/trip/vehicle data caching
 * - Action queue with prioritization for store-and-forward
 * - Timestamps preserved for accurate server-side recording
 * 
 * @module services/offlineStorage
 */

// Storage keys
const STORAGE_KEYS = {
  ROUTE_DATA: 'offline_route_data',
  TRIP_DATA: 'offline_trip_data',
  VEHICLE_DATA: 'offline_vehicle_data',
  PENDING_ACTIONS: 'offline_pending_actions',
  LAST_SYNC: 'offline_last_sync',
};

// Action priority levels (lower = higher priority, syncs first)
export const ActionPriority = {
  CRITICAL: 1,    // Trip status changes, stop arrivals/departures, shift events
  HIGH: 2,        // Route actions, break events
  NORMAL: 3,      // Chat messages, acknowledgments
};

// Map action types to priorities
// NOTE: Only actions that get server ACKs should be queued for store-and-forward.
// GPS_UPDATE and location are fire-and-forget (server doesn't ACK) - not queued.
const ACTION_PRIORITY_MAP = {
  // Critical - must sync first (all get server ACKs)
  'FIXEDSTOP_ARRIVE': ActionPriority.CRITICAL,
  'FIXEDSTOP_DEPART': ActionPriority.CRITICAL,
  'LOOPED_ROUTE_BEGIN': ActionPriority.CRITICAL,
  'LOOPED_ROUTE_END': ActionPriority.CRITICAL,
  'LOOPED_ROUTE_BREAK': ActionPriority.CRITICAL,
  'LOOPED_ROUTE_RESTART': ActionPriority.CRITICAL,
  'ITINERARY_ACTION': ActionPriority.CRITICAL,
  'Message': ActionPriority.CRITICAL, // Trip status messages (ACPTM, REJCM, etc.)
  'openShift': ActionPriority.CRITICAL,
  'completeShift': ActionPriority.CRITICAL,
  'postInspection': ActionPriority.CRITICAL,
  
  // High priority
  'joinRoute': ActionPriority.HIGH,
  'joinRouteGroup': ActionPriority.HIGH,
  'LOOPED_ROUTE_DELETE_BREAK': ActionPriority.HIGH,
  
  // Normal priority
  'chat:sendMessage': ActionPriority.NORMAL,
  'driver:sendMessage': ActionPriority.NORMAL,
  'ACK': ActionPriority.NORMAL,
  
  // NOTE: GPS_UPDATE and location are NOT queued - they're fire-and-forget
  // because the server doesn't send acknowledgments for them.
};

// Maximum age for cached route data (4 hours in ms)
const MAX_ROUTE_DATA_AGE_MS = 4 * 60 * 60 * 1000;

/**
 * Generate a unique ID for actions
 */
const generateActionId = () => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Get priority for an action type
 */
const getActionPriority = (actionType) => {
  return ACTION_PRIORITY_MAP[actionType] || ActionPriority.NORMAL;
};

/**
 * Offline Storage Class
 * Singleton pattern for consistent access across the app
 */
class OfflineStorageService {
  constructor() {
    this.listeners = [];
  }

  // ============================================================================
  // ROUTE DATA PERSISTENCE
  // ============================================================================

  /**
   * Save route data to localStorage with timestamp
   * @param {Object} routeData - Route details object
   */
  saveRouteData(routeData) {
    try {
      const payload = {
        data: routeData,
        timestamp: Date.now(),
        date: new Date().toISOString().split('T')[0], // YYYY-MM-DD
      };
      localStorage.setItem(STORAGE_KEYS.ROUTE_DATA, JSON.stringify(payload));
      this.notifyListeners('routeData', payload);
    } catch (e) {
      console.error('[OfflineStorage] Failed to save route data:', e);
    }
  }

  /**
   * Get cached route data if valid (not stale, same day)
   * @returns {Object|null} Route data or null if invalid/missing
   */
  getRouteData() {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.ROUTE_DATA);
      if (!stored) return null;

      const payload = JSON.parse(stored);
      const now = Date.now();
      const today = new Date().toISOString().split('T')[0];

      // Validate: not too old and same day
      if (now - payload.timestamp > MAX_ROUTE_DATA_AGE_MS) {
        console.log('[OfflineStorage] Route data expired (age)');
        this.clearRouteData();
        return null;
      }

      if (payload.date !== today) {
        console.log('[OfflineStorage] Route data expired (different day)');
        this.clearRouteData();
        return null;
      }

      return payload.data;
    } catch (e) {
      console.error('[OfflineStorage] Failed to get route data:', e);
      return null;
    }
  }

  /**
   * Clear cached route data
   */
  clearRouteData() {
    localStorage.removeItem(STORAGE_KEYS.ROUTE_DATA);
  }

  // ============================================================================
  // ACTION QUEUE (Store-and-Forward)
  // ============================================================================

  /**
   * Queue an action for later sync
   * Actions are timestamped at queue time (when action occurred)
   * 
   * @param {Object} action - Action to queue
   * @param {string} action.type - Event/action type (e.g., 'Message', 'GPS_UPDATE')
   * @param {*} action.data - Action payload
   * @param {string} [action.messageFormat] - For Message events, the pipe-delimited format
   * @returns {string} Action ID
   */
  queueAction(action) {
    try {
      const pending = this.getPendingActions();
      const priority = getActionPriority(action.type);
      
      const queuedAction = {
        id: generateActionId(),
        type: action.type,
        data: action.data,
        messageFormat: action.messageFormat || null,
        priority,
        timestamp: Date.now(),           // When action occurred (for server recording)
        queuedAt: new Date().toISOString(), // Human-readable queue time
        retryCount: 0,
        lastError: null,
      };

      pending.push(queuedAction);
      
      // Sort by priority (critical first), then by timestamp (oldest first)
      pending.sort((a, b) => {
        if (a.priority !== b.priority) {
          return a.priority - b.priority;
        }
        return a.timestamp - b.timestamp;
      });

      localStorage.setItem(STORAGE_KEYS.PENDING_ACTIONS, JSON.stringify(pending));
      this.notifyListeners('actionQueued', queuedAction);
      
      console.log(`[OfflineStorage] Queued action: ${action.type} (priority: ${priority})`);
      return queuedAction.id;
    } catch (e) {
      console.error('[OfflineStorage] Failed to queue action:', e);
      return null;
    }
  }

  /**
   * Get all pending actions sorted by priority then timestamp
   * @returns {Array} Pending actions
   */
  getPendingActions() {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.PENDING_ACTIONS);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('[OfflineStorage] Failed to get pending actions:', e);
      return [];
    }
  }

  /**
   * Get pending actions filtered by priority
   * @param {number} maxPriority - Maximum priority level to include
   * @returns {Array} Filtered pending actions
   */
  getPendingActionsByPriority(maxPriority) {
    return this.getPendingActions().filter(a => a.priority <= maxPriority);
  }

  /**
   * Get count of pending actions
   * @returns {number} Count
   */
  getPendingCount() {
    return this.getPendingActions().length;
  }

  /**
   * Get count of critical pending actions
   * @returns {number} Count of critical actions
   */
  getCriticalPendingCount() {
    return this.getPendingActions().filter(a => a.priority === ActionPriority.CRITICAL).length;
  }

  /**
   * Remove a successfully synced action
   * @param {string} actionId - Action ID to remove
   */
  removeAction(actionId) {
    try {
      const pending = this.getPendingActions().filter(a => a.id !== actionId);
      localStorage.setItem(STORAGE_KEYS.PENDING_ACTIONS, JSON.stringify(pending));
      this.notifyListeners('actionRemoved', actionId);
    } catch (e) {
      console.error('[OfflineStorage] Failed to remove action:', e);
    }
  }

  /**
   * Update action retry count and error
   * @param {string} actionId - Action ID
   * @param {string} error - Error message
   */
  updateActionRetry(actionId, error) {
    try {
      const pending = this.getPendingActions();
      const action = pending.find(a => a.id === actionId);
      if (action) {
        action.retryCount++;
        action.lastError = error;
        action.lastRetryAt = new Date().toISOString();
        localStorage.setItem(STORAGE_KEYS.PENDING_ACTIONS, JSON.stringify(pending));
      }
    } catch (e) {
      console.error('[OfflineStorage] Failed to update action retry:', e);
    }
  }

  /**
   * Clear all pending actions (use with caution)
   */
  clearPendingActions() {
    localStorage.removeItem(STORAGE_KEYS.PENDING_ACTIONS);
    this.notifyListeners('actionsCleared', null);
  }

  // ============================================================================
  // SYNC STATUS
  // ============================================================================

  /**
   * Update last sync timestamp
   */
  updateLastSync() {
    localStorage.setItem(STORAGE_KEYS.LAST_SYNC, Date.now().toString());
  }

  /**
   * Get last sync timestamp
   * @returns {number|null} Timestamp or null
   */
  getLastSync() {
    const stored = localStorage.getItem(STORAGE_KEYS.LAST_SYNC);
    return stored ? parseInt(stored, 10) : null;
  }

  // ============================================================================
  // OPTIMISTIC STATE MANAGEMENT
  // ============================================================================

  /**
   * Apply optimistic update to cached route data
   * Used when offline to reflect user actions immediately
   * 
   * @param {Function} updateFn - Function that receives current data and returns updated data
   */
  applyOptimisticUpdate(updateFn) {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.ROUTE_DATA);
      if (!stored) {
        console.warn('[OfflineStorage] applyOptimisticUpdate: No stored route data found');
        return;
      }

      const payload = JSON.parse(stored);
      console.log('[OfflineStorage] applyOptimisticUpdate: Before update, itinerary steps:', payload.data?.itinerary?.length);
      
      payload.data = updateFn(payload.data);
      payload.optimisticUpdate = true;
      payload.optimisticAt = Date.now();
      
      console.log('[OfflineStorage] applyOptimisticUpdate: After update, itinerary steps:', payload.data?.itinerary?.length);
      
      localStorage.setItem(STORAGE_KEYS.ROUTE_DATA, JSON.stringify(payload));
      console.log('[OfflineStorage] applyOptimisticUpdate: Saved to localStorage, notifying listeners');
      this.notifyListeners('optimisticUpdate', payload.data);
    } catch (e) {
      console.error('[OfflineStorage] Failed to apply optimistic update:', e);
    }
  }

  /**
   * Check if current route data has optimistic updates pending sync
   * @returns {boolean}
   */
  hasOptimisticUpdates() {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.ROUTE_DATA);
      if (!stored) return false;
      const payload = JSON.parse(stored);
      return payload.optimisticUpdate === true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Clear optimistic flag after server sync (server-wins)
   */
  clearOptimisticFlag() {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.ROUTE_DATA);
      if (!stored) return;
      const payload = JSON.parse(stored);
      delete payload.optimisticUpdate;
      delete payload.optimisticAt;
      localStorage.setItem(STORAGE_KEYS.ROUTE_DATA, JSON.stringify(payload));
    } catch (e) {
      console.error('[OfflineStorage] Failed to clear optimistic flag:', e);
    }
  }

  // ============================================================================
  // LISTENERS
  // ============================================================================

  /**
   * Subscribe to storage changes
   * @param {Function} callback - Called with (eventType, data)
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  /**
   * Notify all listeners of a change
   */
  notifyListeners(eventType, data) {
    this.listeners.forEach(cb => {
      try {
        cb(eventType, data);
      } catch (e) {
        console.error('[OfflineStorage] Listener error:', e);
      }
    });
  }

  // ============================================================================
  // DIAGNOSTICS
  // ============================================================================

  /**
   * Get storage diagnostics for debugging
   * @returns {Object} Diagnostic info
   */
  getDiagnostics() {
    const pending = this.getPendingActions();
    const gpsQueue = this.getGPSQueue();
    
    return {
      pendingActions: pending.length,
      criticalActions: pending.filter(a => a.priority === ActionPriority.CRITICAL).length,
      highPriorityActions: pending.filter(a => a.priority === ActionPriority.HIGH).length,
      normalActions: pending.filter(a => a.priority === ActionPriority.NORMAL).length,
      lowPriorityActions: pending.filter(a => a.priority === ActionPriority.LOW).length,
      gpsQueueSize: gpsQueue.length,
      lastSync: this.getLastSync(),
      hasRouteData: !!this.getRouteData(),
      hasOptimisticUpdates: this.hasOptimisticUpdates(),
      oldestPendingAction: pending.length > 0 ? pending[0].queuedAt : null,
    };
  }
}

// Export singleton instance
export const offlineStorage = new OfflineStorageService();

// Also export the class for testing
export { OfflineStorageService, STORAGE_KEYS, ACTION_PRIORITY_MAP };
